import React, { useEffect, useState } from "react";
import {
  Box,
  Chip,
  Grid,
  Typography,
  Paper,
  Divider,
  CircularProgress,
  Button
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import { themes } from "../../theme";
import { LogoVaxYes, ApiImage } from "../../components";
import { makeStyles } from "@material-ui/styles";
import { POST_LOOKUP_CERTIFICATE, POST_VAX_UPDATE_GROUP_CODE } from "../../queries";
import { useMutation } from "@apollo/react-hooks";
import { useHistory, useLocation } from "react-router-dom";
import { useConsumerAuth } from "../../hooks";
import { CheckCircle, CalendarToday, Schedule } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { VaxWalletPass } from "./VaxWalletPass";
import { isAndroid } from "react-device-detect";
import * as isEmpty from "lodash.isempty";
import { Alert } from "@material-ui/lab";

const useClasses = makeStyles(theme => ({
  doseChip: {
    fontWeight: 700,
    backgroundColor: themes.GGV.palette.primary.main,
    color: theme.palette.common.white
  },
  successIcon: {
    color: theme.palette.success.main
  }
}));

const getBoosterIndex = certificates =>
  certificates.length === 2 &&
  certificates[0].service_code === "COVID_19_VACCINE_JNJ"
    ? 1
    : 2;

const VaccineDoseInfo = ({ doseNo, dose, certificates }) => {
  const { t } = useTranslation();
  const classes = useClasses();

  if(certificates[doseNo-1].service_code === "empty") {
    return (
      <Box mb={1}></Box>
    )
  }

  return (
    <>
      {getBoosterIndex(certificates) < doseNo && (
        <div>
          <Divider style={{ width: '200%' }} />
          <br />
        </div>
      )}
      {certificates.service_code != "empty" && (
      <Box mb={1}>
        <Chip
          label={
            doseNo === getBoosterIndex(certificates) + 1
              ? "Booster"
              : `Dose ${doseNo}`
          }
          className={classes.doseChip}
        />
      </Box>
      )}
      {dose.org_name && (
        <Typography color="inherit" gutterBottom>
          <strong>
            {t("appointment.vax_certificate.card_title")} {dose.org_name}
          </strong>
        </Typography>
      )}
      <Typography color="inherit">
        {t("appointment.vax_certificate.card_subtitle")}: {dose.brand}
      </Typography>
      {dose.lot_no && (
        <Typography color="inherit">Lot #: {dose.lot_no}</Typography>
      )}
      <Box mt={1}>
        <Grid container alignItems="center" spacing={1}>
          <Grid item>
            <CalendarToday
              style={{ fontSize: "16px", verticalAlign: "text-bottom" }}
            />
          </Grid>
          <Grid item xs>
            <Typography color="inherit">
              {moment(dose.appointment_date).format("ddd, D MMM YYYY")}
            </Typography>
          </Grid>
        </Grid>
        {dose.appointment_time && (
          <Grid container alignItems="center" spacing={1}>
            <Grid item>
              <Schedule
                style={{ fontSize: "16px", verticalAlign: "text-bottom" }}
              />
            </Grid>
            <Grid item xs>
              <Typography color="inherit">
                {moment(dose.appointment_time, "HH:mm:ss").format("h:mm A")}
              </Typography>
            </Grid>
          </Grid>
        )}
      </Box>
    </>
  );
};

const Covid19VaccineCertificate = () => {
  const { t } = useTranslation();
  const classes = useClasses();
  const {
    phone_number,
    dob,
    first_name,
    last_name,
    session_token
  } = useConsumerAuth();
  const [certificates, setCertificates] = useState([]);
  const [booster, setBooster] = useState({});

  const history = useHistory();
  const { state = {} } = useLocation();

  const group_code = sessionStorage.getItem("Group_Code");

  const [postVaxUpdateGroupCode, { }] = useMutation(
    POST_VAX_UPDATE_GROUP_CODE,
    {
      onCompleted: () => {
      }
    }
  )

  const [postLookupCertificate, { loading }] = useMutation(
    POST_LOOKUP_CERTIFICATE,
    {
      onCompleted: ({ certificate }) => {
        if (
          certificate &&
          certificate.status &&
          certificate.status === "success" &&
          certificate.results &&
          Array.isArray(certificate.results.certificates)
        ) {
          var boosterIndex = getBoosterIndex(certificate.results.certificates)
          if(boosterIndex === 1) {
            certificate.results.certificates.splice(boosterIndex, 0, { service_code: "empty" })
          }
          setCertificates(certificate.results.certificates);

          if (group_code) {
            postVaxUpdateGroupCode({
                variables: {
                  body: {
                    group_code,
                    first_name,
                    last_name,
                    dob,
                    phone_number,
                    token: session_token
                  }
                }
              });
          }
        }
      }
    }
  );

  useEffect(() => {
    var boosterIndex = getBoosterIndex(certificates)
    if (certificates[boosterIndex]) {
      setBooster(certificates[boosterIndex]);
    }
  }, [certificates]);

  useEffect(() => {
    if (!phone_number || !dob || !first_name || !last_name || !session_token)
      return;

    postLookupCertificate({
      variables: {
        body: {
          phone_number,
          dob,
          first_name,
          last_name,
          token: session_token
        }
      }
    });
  }, []);

  /**
   * Return highest verification level from one or more certificates
   *
   * @param {array} certificates
   * @returns {number}
   */
  const certificatesHighestLevel = certificates =>
    certificates
      .map(certificate => certificate.verification_level)
      .reduce((control, compare) => (compare > control ? compare : control));

  return (
    <Paper variant="outlined" data-cy="vaccine-certificate">
      <Box p={2}>
        <Grid container spacing={1} alignItems="center">
          <Grid item>
            <LogoVaxYes height="30px" />
          </Grid>
          <Grid item xs>
            <Typography component="h1" variant="h5">
              {t("appointment.vax_certificate.header_title")}
            </Typography>
          </Grid>
        </Grid>
      </Box>
      <Divider />
      <Box p={2}>
        {isEmpty(booster) && state.boosterAdded && (
          <Alert severity="info">
            {t("appointment.vax_certificate.booster_added_alert")}
          </Alert>
        )}
        <Box my={1}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={6}>
              <Typography component="h2" variant="h5" color="inherit">
                <strong>
                  {first_name} {last_name}
                </strong>
              </Typography>
              <Typography component="h3" variant="h6" color="inherit">
                {dob}
              </Typography>
            </Grid>
            {!isAndroid && (
              <Grid item xs={6}>
                <VaxWalletPass type="ip" />
              </Grid>
            )}
          </Grid>
        </Box>
        {loading ? (
          <CircularProgress size="small" />
        ) : (
          <>
            {certificates.length > 0 ? (
              <>
                {isEmpty(booster) &&
                  certificatesHighestLevel(certificates) > 0 &&
                  !state.boosterAdded && (
                    <Box my={2}>
                      <Button
                        data-cy="add-booster-button"
                        variant="contained"
                        size="small"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={() => {
                          history.push({
                            pathname: "/vaxyes/booster",

                            state: { noSkip: true }
                          });
                        }}
                      >
                        {t("appointment.vax_certificate.add_booster_button")}
                      </Button>
                    </Box>
                  )}
                <Grid container spacing={2}>
                  {certificates.map((certificate, index) => (
                    <Grid item xs={6} key={`dose-${index}`}>
                      <VaccineDoseInfo
                        dose={certificate}
                        doseNo={index + 1}
                        certificates={certificates}
                      />
                    </Grid>
                  ))}
                {!isAndroid && !isEmpty(booster) && (
                  <Grid item xs={6}>
                    <br />
                    <VaxWalletPass type="ib" />
                  </Grid>
                )}

                </Grid>
                <Box my={2}>
                  <Grid container spacing={1} alignItems="center">
                    <Grid item>
                      <CheckCircle
                        style={{
                          fontSize: "16px",
                          verticalAlign: "text-bottom"
                        }}
                        className={classes.successIcon}
                      />
                    </Grid>
                    <Grid item xs>
                      {t("appointment.vax_certificate.certification_level", {
                        level: certificatesHighestLevel(certificates)
                      })}
                    </Grid>
                  </Grid>
                </Box>
                {/* {isAndroid && (
                  <Box my={2} textAlign="center">
                    <VaxWalletPass type="a" />
                  </Box>
                )} */}
                {certificates.map((certificate, index) => (
                  <Box my={1} key={`cert-images-${index}`}>
                    {Array.isArray(certificate.images) &&
                      certificate.images.length > 0 &&
                      certificate.images.map((imagePath, index) => (
                        <ApiImage
                          key={`cert-image-${index}`}
                          path={imagePath}
                        />
                      ))}
                  </Box>
                ))}
              </>
            ) : (
              <Typography color="error">
                {t("appointment.vax_certificate.error_empty")}
              </Typography>
            )}
          </>
        )}
      </Box>
    </Paper>
  );
};

export { Covid19VaccineCertificate };
